import api from "./api";
import {
  FuncionarioCreate,
  PaginatedMembrosResponse,
  MembroFranquiaAPI,
  CargoFranquia,
} from "../types/Funcionario";

export interface NovoMembroFranquia {
  email: string;
  nome_completo: string;
  password: string;
  cpf: string;
  cargos: string[];
}

export interface MembroFranquia {
  id: number;
  email: string;
  nome_completo: string;
  cpf: string;
  cargos: string[];
  status: string;
  created: string;
  modified: string;
}

export interface PaginatedMembrosList {
  count: number;
  next?: string;
  previous?: string;
  results: MembroFranquia[];
}

export class FuncionarioFranquiaService {
  /**
   * Adiciona um novo membro à franquia
   * @param franquiaId ID da franquia
   * @param data Dados do novo membro
   */
  async adicionarMembro(
    franquiaId: number,
    data: NovoMembroFranquia
  ): Promise<MembroFranquia> {
    const response = await api.post(
      `/api/franquias/${franquiaId}/membros/`,
      data
    );
    return response.data;
  }

  /**
   * Lista membros da franquia
   * @param franquiaId ID da franquia
   * @param params Parâmetros de paginação, ordenação e filtro
   */
  async listarMembros(
    franquiaId: number,
    params: {
      limit?: number;
      offset?: number;
      ordering?: string;
      filtro?: string;
      cargo?: string;
      status?: string;
    } = {}
  ): Promise<PaginatedMembrosResponse> {
    const searchParams = new URLSearchParams();

    if (params.limit) {
      searchParams.append("limit", params.limit.toString());
    }
    if (params.offset) {
      searchParams.append("offset", params.offset.toString());
    }
    if (params.ordering) {
      searchParams.append("ordering", params.ordering);
    }
    if (params.filtro) {
      searchParams.append("filtro", params.filtro);
    }
    if (params.cargo) {
      searchParams.append("cargo", params.cargo);
    }
    if (params.status) {
      if (params.status === "BLOQUEADO") {
        searchParams.append("is_bloqueado", "true");
      } else {
        searchParams.append("status", params.status);
      }
    }

    const response = await api.get(
      `/api/franquias/${franquiaId}/membros/?${searchParams.toString()}`
    );
    return response.data;
  }

  /**
   * Bloqueia ou desbloqueia um membro da franquia
   * @param franquiaId ID da franquia
   * @param membroId ID do membro
   * @param bloquear true para bloquear, false para desbloquear
   */
  async alterarStatusMembro(
    franquiaId: number,
    membroId: number,
    bloquear: boolean
  ): Promise<MembroFranquiaAPI> {
    const response = await api.patch(
      `/api/franquias/${franquiaId}/membros/${membroId}/`,
      {
        user: {
          is_bloqueado: bloquear,
        },
      }
    );
    return response.data;
  }

  /**
   * Exclui um membro da franquia (marca como INATIVO)
   * @param franquiaId ID da franquia
   * @param membroId ID do membro
   */
  async excluirMembro(
    franquiaId: number,
    membroId: number
  ): Promise<MembroFranquiaAPI> {
    const response = await api.patch(
      `/api/franquias/${franquiaId}/membros/${membroId}/`,
      {
        status: "INATIVO",
      }
    );
    return response.data;
  }

  /**
   * Atualiza os cargos de um membro da franquia
   * @param franquiaId ID da franquia
   * @param membroId ID do membro
   * @param cargos Lista de cargos
   */
  async atualizarCargos(
    franquiaId: number,
    membroId: number,
    cargos: CargoFranquia[]
  ): Promise<MembroFranquiaAPI> {
    const response = await api.patch(
      `/api/franquias/${franquiaId}/membros/${membroId}/`,
      {
        cargos: cargos,
      }
    );
    return response.data;
  }

  /**
   * Obtém detalhes de um membro específico
   * @param franquiaId ID da franquia
   * @param membroId ID do membro
   */
  async obterMembro(
    franquiaId: number,
    membroId: number
  ): Promise<MembroFranquiaAPI> {
    const response = await api.get(
      `/api/franquias/${franquiaId}/membros/${membroId}/`
    );
    return response.data;
  }

  /**
   * Atualiza um membro da franquia
   * @param franquiaId ID da franquia
   * @param membroId ID do membro
   * @param data Dados para atualização
   */
  async atualizarMembro(
    franquiaId: number,
    membroId: number,
    data: Partial<NovoMembroFranquia>
  ): Promise<MembroFranquia> {
    const response = await api.patch(
      `/api/franquias/${franquiaId}/membros/${membroId}/`,
      data
    );
    return response.data;
  }

  /**
   * Remove um membro da franquia
   * @param franquiaId ID da franquia
   * @param membroId ID do membro
   */
  async removerMembro(franquiaId: number, membroId: number): Promise<void> {
    await api.delete(`/api/franquias/${franquiaId}/membros/${membroId}/`);
  }

  /**
   * Converte dados do formulário para o formato da API
   * @param formData Dados do formulário
   */
  static formatarDadosParaAPI(formData: FuncionarioCreate): NovoMembroFranquia {
    return {
      email: formData.email,
      nome_completo: formData.nome_completo,
      password: formData.password,
      cpf: formData.cpf,
      cargos: formData.cargos,
    };
  }
}

export const funcionarioFranquiaService = new FuncionarioFranquiaService();
